import { useMutation } from "@apollo/client";
import { TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useState } from "react";

export const CLOSE_EMANIFEST_MISSING_AS_NEVER_IN_EMANIFEST = gql(`
  mutation CloseEManifestMissingAsNeverInEManifest($discrepancyIds: [ID!]!, $note: String!) {
    closeEManifestMissingAsNeverInEManifest(discrepancyIds: $discrepancyIds, note: $note) {
      id
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

type Props = {
  open: boolean;
  discrepancyId: string;
  /** For the subheader, so staff can confirm which manifest they're closing. */
  manifestTrackingNumber?: string | null;
  onClose: () => void;
  /** Called after a successful close (the dialog closes itself first). */
  onClosed?: () => void;
};

/**
 * Staff-only close for a PENDING, OVERDUE_60 eManifestMissing discrepancy that
 * will never be observed in e-Manifest (e.g. the receiving facility isn't a
 * registered TSDF and will never upload a record). Resolves the row with
 * resolutionPath NEVER_IN_EMANIFEST — a resolution, not a dismissal, and with
 * no undo. Mount only while open (`{open && <CloseNeverInEManifestDialog …/>}`)
 * so each opening starts with fresh state, matching MarkBlockedDialog.
 */
export function CloseNeverInEManifestDialog({
  open,
  discrepancyId,
  manifestTrackingNumber,
  onClose,
  onClosed,
}: Props) {
  const [note, setNote] = useState("");
  const alerts = useAlerts();
  const noteMissing = note.trim() === "";

  const [closeNeverInEManifest, { loading }] = useMutation(
    CLOSE_EMANIFEST_MISSING_AS_NEVER_IN_EMANIFEST,
    {
      refetchQueries: [
        "BulkManifestDiscrepancies",
        "ProcessedManifests",
        "ComplianceManifestData",
        "DiscrepancyEvents",
        // The writer calls unblockCompletedManifests, so this close can clear
        // a blocked manifest's last open item — refetch the block chip too.
        "ManifestBlockState",
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleSubmit = async () => {
    if (noteMissing) return;
    try {
      await closeNeverInEManifest({
        variables: { discrepancyIds: [discrepancyId], note: note.trim() },
      });
      alerts.success("Marked as will never be in e-Manifest");
      onClose();
      onClosed?.();
    } catch (e) {
      alerts.error("Failed to close as never in e-Manifest", e instanceof Error ? e : undefined);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Will never be in e-Manifest"
      subheader={manifestTrackingNumber ?? undefined}
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Closing...",
        hideStartIcon: true,
        saveText: "Close discrepancy",
        disabled: loading || noteMissing,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <Typography variant="body2" color="text.secondary">
        Closes this as resolved instead of pending forever — e.g. the receiving facility isn&apos;t
        a registered TSDF and will never upload a record. This cannot be undone, and an e-Manifest
        record arriving later will not reopen it.
      </Typography>
      <TextField
        label="Note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
        error={noteMissing}
        helperText="Required — explain why this will never be in e-Manifest."
      />
    </Dialog>
  );
}

/**
 * Self-guarding wrapper so the caller can mount this unconditionally
 * (`<CloseNeverInEManifestDialogSlot target={…} … />`) instead of an inline
 * `target &&` in its own render — keeps that null-check out of an
 * already-complex container component's own branch count.
 */
export function CloseNeverInEManifestDialogSlot({
  target,
  onClose,
  onClosed,
}: {
  target: { id: string; manifestTrackingNumber: string | null } | null;
  onClose: () => void;
  onClosed?: () => void;
}) {
  if (!target) return null;
  return (
    <CloseNeverInEManifestDialog
      open
      discrepancyId={target.id}
      manifestTrackingNumber={target.manifestTrackingNumber}
      onClose={onClose}
      onClosed={onClosed}
    />
  );
}
